import axios, { type AxiosRequestConfig } from "axios";

import { env } from "@/config/env";
import { clearStorage, getStorage } from "@/lib/storage";
import type { ApiEnvelope } from "@/types/api";
import type { AuthTokens } from "@/types/partner";

import { ApiError, parseApiError } from "./errors";

const SESSION_KEY = "session";

type SessionSlice = {
  tokens: AuthTokens;
};

const http = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 45_000,
  headers: {
    Accept: "application/json",
  },
});

http.interceptors.request.use((config) => {
  const session = getStorage<SessionSlice>(SESSION_KEY);
  const token = session?.tokens?.access;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status as number | undefined;
    const url = String(error.config?.url ?? "");
    const isGuestAuthRequest =
      url.includes("/partner/auth/login/") ||
      url.includes("/partner/auth/reset-password/") ||
      url.includes("/partner/auth/verify-password-reset/");
    // Don't wipe / redirect on failed guest auth attempts.
    if (status === 401 && !isGuestAuthRequest) {
      clearStorage();
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    const parsed = parseApiError(error.response?.data, status);
    return Promise.reject(parsed);
  },
);

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<ApiEnvelope<T> | T>(config);
  const body = response.data;

  if (body && typeof body === "object" && "success" in body) {
    const envelope = body as ApiEnvelope<T>;
    if (envelope.success === false) {
      throw parseApiError(envelope, envelope.status ?? response.status);
    }

    // Paginated list: { success, count, page, total_pages, page_size, results: [...] }
    if (Array.isArray(envelope.results) && "count" in body) {
      const {
        success: _success,
        status: _status,
        message: _message,
        error: _error,
        ...rest
      } = body as Record<string, unknown>;
      return rest as T;
    }

    if ("results" in body) {
      return envelope.results;
    }

    // Non-list success payloads (document upload/metadata, etc.)
    const {
      success: _success,
      status: _status,
      message: _message,
      error: _error,
      ...rest
    } = body as Record<string, unknown>;
    return rest as T;
  }

  return body as T;
}

export const api = {
  get<T>(url: string, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: "GET", url });
  },
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: "POST", url, data });
  },
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: "PUT", url, data });
  },
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: "PATCH", url, data });
  },
  delete<T>(url: string, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: "DELETE", url });
  },
};

export { ApiError };
